import Validator from '../../../../utils/Validator';
import ListenerItem from '../../../../utils/ListenerItem';
import EventListenerManager from '../../../../utils/EventListenerManager';
import HtmHelper from '../../../../utils/HtmHelper';
import ItmMgr from '../../../../gui/ItmMgr';

/**
 * class ColumnWidthManager - manages the width (and interactive width adjustment) of a
 * table column ({@link XtwCol}). It is a plain delegate: the host column is passed in and
 * accessed via {@link #host}. It extends ListenerItem so it owns the width-adjustment
 * listeners, which bind to the host column's span / drag elements.
 */
export default class ColumnWidthManager extends ListenerItem {

	/**
	 * constructs a new instance
	 * @param {*} host the host column (XtwCol)
	 */
	constructor( host ) {
		super( 'widgets.xtw.column.ColumnWidthManager' );
		this._host = host;
		/** transient state for the interactive width-adjustment gesture */
		this.columnWidthOnWidthAdjustmentSpanMouseDown = void 0;
		this.timestampOnWidthAdjustmentSpanMouseDown = void 0;
		this.dateNowOnWidthAdjustmentSpanMouseDown = void 0;
	}

	/**
	 * @returns {*} the host column
	 */
	get host() {
		return this._host;
	}

	/**
	 * the host column's DOM element. Exposed because this manager registers listeners
	 * (via {@link ListenerItem#addPrefixListener}); when no explicit element is given the
	 * listener binds to this element - matching the former mixin behavior.
	 * @returns {HTMLElement|undefined} the host's DOM element
	 */
	get element() {
		return this.host.element;
	}

	/**
	 * @returns {Boolean} true if the host column is rendered
	 */
	get isRendered() {
		return this.host.isRendered;
	}

	/**
	 * @override
	 */
	doDestroy() {
		this._host = void 0;
		super.doDestroy();
	}

	/**
	 * @returns {HTMLElement|undefined} the column drag widget's element (from the table)
	 */
	get columnDragElement() {
		const tableWidget = this.host.xtdTbl;
		return Validator.isObject( tableWidget ) ? tableWidget.cdrElm : void 0;
	}

	setWidth( width ) {
		if ( !Validator.isPositiveNumber( width ) ) {
			return false;
		}
		const host = this.host;
		const widthDifference = width - host.width;
		host.width = width;
		host.update();
		if ( !host.visible ) {
			return false;
		}
		if ( !Validator.isObject( host.xthWdg ) ||
			!Validator.isFunction( host.xthWdg._renderAllCols ) ) {
			return false;
		}
		// let the table head instance render all the columns
		host.xthWdg._renderAllCols();
		if ( Validator.isObject( host.xthWdg.xtwBody ) ) {
			// notify the table body instance
			host.xthWdg.xtwBody.onColumnWidth( host, width, widthDifference );
		}
		return true;
	}

	setTemporaryWidthProperties( domEvent, returnValue ) {
		this.columnWidthOnWidthAdjustmentSpanMouseDown = Number( this.host.width );
		if ( domEvent instanceof Event ) {
			this.timestampOnWidthAdjustmentSpanMouseDown = Number( domEvent.timeStamp );
		}
		this.dateNowOnWidthAdjustmentSpanMouseDown = Number( Date.now() );
		return returnValue;
	}

	removeTemporaryWidthProperties( returnValue ) {
		this.columnWidthOnWidthAdjustmentSpanMouseDown = void 0;
		this.timestampOnWidthAdjustmentSpanMouseDown = void 0;
		this.dateNowOnWidthAdjustmentSpanMouseDown = void 0;
		return returnValue;
	}

	columnWidthChanged() {
		return this.columnWidthOnWidthAdjustmentSpanMouseDown != this.host.width;
	}

	logTimePassed() {
		const now = Number( Date.now() );
		return !Validator.isPositiveNumber( this.dateNowOnWidthAdjustmentSpanMouseDown ) ||
			now - this.dateNowOnWidthAdjustmentSpanMouseDown > 400;
	}

	addColumnDragWidgetMouseUpListener() {
		const columnDragElement = this.columnDragElement;
		if ( !( columnDragElement instanceof HTMLElement ) ) {
			return false;
		}
		return Validator.isObject(this.host.spans) && this.addPrefixListener("mouseup", "onColumnDragWidgetMouseUp", "autoWidth", columnDragElement,  false);
	}

	removeColumnDragWidgetMouseUpListener() {
		const columnDragElement = this.columnDragElement;
		if ( !( columnDragElement instanceof HTMLElement ) ) {
			return false;
		}
		const successfullyRemoved = EventListenerManager.removeListener( this, "mouseup", columnDragElement, "autoWidth" );
		return successfullyRemoved;
	}

	addWidthAdjustmentSpanMouseDownListener() {
		const spans = this.host.spans;
		return Validator.isObject(spans) && this.addPrefixListener("mousedown", "onWaSpanMouseDown", "autoWidth", spans.wa, false);
	}

	removeWidthAdjustmentSpanMouseDownListener() {
		const spans = this.host.spans;
		const successfullyRemoved = Validator.isObject( spans ) &&
			EventListenerManager.removeListener( this, "mousedown", spans.wa, "autoWidth" );
		return successfullyRemoved;
	}

	addWidthAdjustmentSpanDoubleClickListener() {
		const spans = this.host.spans;
		return Validator.isObject(spans) && this.addPrefixListener("dblclick", "onWidthAdjustmentSpanDoubleClick", "autoWidth", spans.wa, false);
	}

	removeWidthAdjustmentSpanDoubleClickListener() {
		const spans = this.host.spans;
		const successfullyRemoved = Validator.isObject( spans ) &&
			EventListenerManager.removeListener( this, "dblclick", spans.wa, "autoWidth" );
		return successfullyRemoved;
	}

	onWidthAdjustmentSpanDoubleClick( domEvent ) {
		this.removeColumnDragWidgetMouseUpListener();
		this.removeTemporaryWidthProperties();
		if ( domEvent instanceof MouseEvent ) {
			domEvent.stopPropagation();
			domEvent.preventDefault();
		}
		const minimalRequiredWidth = this._getMinimalRequiredWidth();
		return this.setWidth( minimalRequiredWidth );
	}

	onWaSpanMouseDown( domEvent ) {
		const listenerAdded = this.addColumnDragWidgetMouseUpListener();
		if ( listenerAdded ) {
			this.setTemporaryWidthProperties( domEvent );
		}
		if ( !listenerAdded ) {
			this.trace( `Could not add a "mouseup" event` +
				` listener to the table column drag widget for auto-width adjustment` +
				` purposes.` );
		}
		return listenerAdded;
	}

	onColumnDragWidgetMouseUp( domEvent ) {
		this.removeColumnDragWidgetMouseUpListener();
		if ( !this.isColumnDragWidgetOverThisWidthSpan() ) {
			return this.removeTemporaryWidthProperties( false );
		}
		if ( this.columnWidthChanged() || this.logTimePassed() ) {
			return this.removeTemporaryWidthProperties( false );
		}
		this.removeTemporaryWidthProperties();
		// in this case we do not want the DOM event to be stopped from
		// propagation, so we won't provide the method with it
		return this.onWidthAdjustmentSpanDoubleClick();
	}

	_getMinimalRequiredWidth() {
		const host = this.host;
		if ( !host.available || !host.visible || !host.isRendered ) {
			return void 0;
		}
		const requiredWidth = this.getRequiredMinimalWidthToDisplayAllContent();
		if ( !Validator.isObject( host.spans ) ) {
			// 4px is the "padding-left" of the first span, another 4px is the width
			// of the "width-adjustment" span, both are indicated in the stylesheet
			return !Validator.isPositiveNumber( requiredWidth, false ) ? void 0 :
				requiredWidth + 8;
		}
		let additionalWidth = 0;
		for ( let spanEntry of [ ...Object.entries( host.spans ) ] ) {
			if ( !Validator.isArray( spanEntry ) || spanEntry.length < 2 ) {
				continue;
			}
			if ( spanEntry[ 0 ] == "st" ) {
				// we do not want to include the "text span" into this calculation
				continue;
			}
			const width = HtmHelper.getNumericAttributePixelWidth( spanEntry[ 1 ] );
			if ( !Validator.isPositiveNumber( width, false ) ) {
				continue;
			}
			additionalWidth += width;
		}
		const minimalRequiredWidth = !Validator.isPositiveNumber( requiredWidth ) ?
			additionalWidth : additionalWidth + requiredWidth;
		// 4px is the "padding-left" of the first span, another 4px is the width
		// of the "width-adjustment" span, both are indicated in the stylesheet
		return !Validator.isPositiveNumber( minimalRequiredWidth, false ) ?
			void 0 : minimalRequiredWidth + 8;
	}

	adjustToMinimalRequiredWidth() {
		const minimalRequiredWidth = this._getMinimalRequiredWidth();
		if ( !Validator.isPositiveNumber( minimalRequiredWidth, false ) ) {
			return false;
		}
		this.host.width = minimalRequiredWidth;
		return true;
	}

	getRequiredMinimalWidthToDisplayAllContent() {
		const spans = this.host.spans;
		if ( !Validator.isObject( spans ) ) {
			return void 0;
		}
		const element = spans.st;
		if ( !( element instanceof HTMLElement ) ) {
			return void 0;
		}
		const itemManager = ItmMgr.getInst();
		if ( !Validator.isObject( itemManager ) ||
			!Validator.isFunction( itemManager.measureText ) ) {
			return void 0;
		}
		const measuredText = itemManager.measureText( element );
		if ( !Validator.isObjectPath( measuredText, "measuredText.sts" ) ||
			!Validator.isPositiveNumber( measuredText.sts.cx ) ) {
			return void 0;
		}
		return Number( measuredText.sts.cx );
	}

	isColumnDragWidgetOverThisWidthSpan() {
		const columnDragElement = this.columnDragElement;
		if ( !( columnDragElement instanceof HTMLElement ) ) {
			return false;
		}
		const spans = this.host.spans;
		const widthAdjusmentSpan = Validator.isObject( spans ) ? spans.wa : void 0;
		if ( !( widthAdjusmentSpan instanceof HTMLElement ) ) {
			return false;
		}
		const spanRect = widthAdjusmentSpan.getBoundingClientRect();
		const regionStartX = spanRect.x;
		const regionEndX = spanRect.x + spanRect.width;
		const dragElementRect = columnDragElement.getBoundingClientRect();
		return regionStartX <= dragElementRect.x && dragElementRect.x <= regionEndX;
	}

}
